package com.example.mystybag.core.views

import androidx.compose.material3.ColorScheme
import androidx.compose.material3.Typography
import androidx.compose.runtime.Composable
import androidx.compose.runtime.compositionLocalOf
import com.arkivanov.decompose.value.MutableValue
import com.example.mystybag.common.resources.SR
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_THEME_CODE
import com.example.mystybag.core.defines.UtilsWalletDefines.THEME_CODE_DARK
import com.example.mystybag.core.defines.UtilsWalletDefines.THEME_CODE_SYSTEM
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.core.extensions.color
import com.example.mystybag.core.ui.theme.LocalTheme.p0
import com.example.mystybag.core.ui.theme.defaultDarkColorScheme
import com.example.mystybag.core.ui.theme.defaultLightColorScheme
import com.example.mystybag.core.ui.theme.getAppThemeTypography
import com.example.mystybag.lib.setup.AppCustomColors
import com.example.mystybag.lib.setup.AppCustomGradients
import com.example.mystybag.lib.setup.MystyBagLibSDK
import com.example.mystybag.lib.setup.NavigationCustomColors
import kotlinx.coroutines.channels.BufferOverflow
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow

private fun resolveIsDarkTheme(): Boolean {
    val code = Preferences.getInt(PREFERENCES_THEME_CODE, 0)
    return when (code) {
        THEME_CODE_DARK -> true
        THEME_CODE_SYSTEM -> MystyBagLibSDK.isDarkMode ?: false
        else -> false
    }
}
val isDarkTheme  : MutableValue<Boolean>             = MutableValue(resolveIsDarkTheme())
var currentTheme : MutableStateFlow<AppThemeColors>  = MutableStateFlow(AppThemeColors())

data class AppThemeColors(
    var colors                  : ColorScheme            = if (isDarkTheme.value) MystyBagLibSDK.darkColorScheme?.invoke(defaultDarkColorScheme) ?: defaultDarkColorScheme else MystyBagLibSDK.lightColorScheme?.invoke(defaultLightColorScheme) ?: defaultLightColorScheme,
    var typography              : Typography             = getAppThemeTypography(colors),
    var customColors            : AppCustomColors        = getCustomColors(colors),
    var navigationCustomColors  : NavigationCustomColors = getCustomNavigationColors(colors, isDarkTheme.value),
    var customGradients         : AppCustomGradients     = getCustomGradients()
)

val AppTheme = compositionLocalOf {
    currentTheme.value
}

val navBarBottomPadding = compositionLocalOf {
    p0
}

private fun getCustomColors(colors: ColorScheme): AppCustomColors {
    return MystyBagLibSDK.customColors?.invoke(colors) ?: AppCustomColors(colors)
}

private fun getCustomNavigationColors(colors: ColorScheme, isDarkMode: Boolean): NavigationCustomColors {
    return MystyBagLibSDK.navigationColors?.invoke(colors, isDarkTheme.value) ?: NavigationCustomColors(colors, isDarkMode)
}

private fun getCustomGradients(): AppCustomGradients {
    return MystyBagLibSDK.customGradients?.invoke() ?: AppCustomGradients()
}

data class SharedColor(
    val color: ColorDefines,
    val alpha: Float = 1f
)

@Composable
fun SharedColor.toColor() = when (this.color) {
    ColorDefines.Background                -> AppTheme.current.colors.background
    ColorDefines.OnBackground              -> AppTheme.current.colors.onBackground
    ColorDefines.Primary                   -> AppTheme.current.colors.primary
    ColorDefines.OnPrimary                 -> AppTheme.current.colors.onPrimary
    ColorDefines.PrimaryContainer          -> AppTheme.current.colors.primaryContainer
    ColorDefines.OnPrimaryContainer        -> AppTheme.current.colors.onPrimaryContainer
    ColorDefines.Secondary                 -> AppTheme.current.colors.secondary
    ColorDefines.OnSecondary               -> AppTheme.current.colors.onSecondary
    ColorDefines.SecondaryContainer        -> AppTheme.current.colors.secondaryContainer
    ColorDefines.OnSecondaryContainer      -> AppTheme.current.colors.onSecondaryContainer
    ColorDefines.Tertiary                  -> AppTheme.current.colors.tertiary
    ColorDefines.OnTertiary                -> AppTheme.current.colors.onTertiary
    ColorDefines.TertiaryContainer         -> AppTheme.current.colors.tertiaryContainer
    ColorDefines.OnTertiaryContainer       -> AppTheme.current.colors.onTertiaryContainer
    ColorDefines.Error                     -> AppTheme.current.colors.error
    ColorDefines.OnError                   -> AppTheme.current.colors.onError
    ColorDefines.ErrorContainer            -> AppTheme.current.colors.errorContainer
    ColorDefines.OnErrorContainer          -> AppTheme.current.colors.onErrorContainer
    ColorDefines.SurfaceDim                -> AppTheme.current.colors.surfaceDim
    ColorDefines.Surface                   -> AppTheme.current.colors.surface
    ColorDefines.SurfaceBright             -> AppTheme.current.colors.surfaceBright
    ColorDefines.InverseSurface            -> AppTheme.current.colors.inverseSurface
    ColorDefines.InverseOnSurface          -> AppTheme.current.colors.inverseOnSurface
    ColorDefines.InversePrimary            -> AppTheme.current.colors.inversePrimary
    ColorDefines.SurfaceContainerLowest    -> AppTheme.current.colors.surfaceContainerLowest
    ColorDefines.SurfaceContainerLow       -> AppTheme.current.colors.surfaceContainerLow
    ColorDefines.SurfaceContainer          -> AppTheme.current.colors.surfaceContainer
    ColorDefines.SurfaceContainerHigh      -> AppTheme.current.colors.surfaceContainerHigh
    ColorDefines.SurfaceContainerHighest   -> AppTheme.current.colors.surfaceContainerHighest
    ColorDefines.OnSurface                 -> AppTheme.current.colors.onSurface
    ColorDefines.OnSurfaceVariant          -> AppTheme.current.colors.onSurfaceVariant
    ColorDefines.Outline                   -> AppTheme.current.colors.outline
    ColorDefines.OutlineVariant            -> AppTheme.current.colors.outlineVariant
    ColorDefines.Scrim                     -> AppTheme.current.colors.scrim
    ColorDefines.AmountGreen               -> AppTheme.current.customColors.amountGreen
    ColorDefines.AlertOrange               -> AppTheme.current.customColors.alertOrange
    ColorDefines.VividBlue                 -> AppTheme.current.customColors.vividBlue
    ColorDefines.VividRed                  -> AppTheme.current.customColors.vividRed
    ColorDefines.VividGreen                -> AppTheme.current.customColors.vividGreen
    ColorDefines.VividPurple               -> AppTheme.current.customColors.vividPurple
    ColorDefines.VividYellow               -> AppTheme.current.customColors.vividYellow
    ColorDefines.BlueToWhite               -> AppTheme.current.customColors.blueToWhite
    ColorDefines.GreenToBlue               -> AppTheme.current.customColors.greenToBlue
    ColorDefines.RedToWhite                -> AppTheme.current.customColors.redToWhite
    ColorDefines.LightGrayToDarkBlue       -> AppTheme.current.customColors.lightGrayToDarkBlue
    ColorDefines.TintIconColor             -> SR.colors.tint_icon_color.color
}.let { color ->
    if (this.alpha != 1f)
        color.copy(alpha = alpha)
    else
        color
}

enum class ColorDefines {
    Background,
    OnBackground,
    Primary,
    OnPrimary,
    PrimaryContainer,
    OnPrimaryContainer,
    Secondary,
    OnSecondary,
    SecondaryContainer,
    OnSecondaryContainer,
    Tertiary,
    OnTertiary,
    TertiaryContainer,
    OnTertiaryContainer,
    Error,
    OnError,
    ErrorContainer,
    OnErrorContainer,
    SurfaceDim,
    Surface,
    SurfaceBright,
    InverseSurface,
    InverseOnSurface,
    InversePrimary,
    SurfaceContainerLowest,
    SurfaceContainerLow,
    SurfaceContainer,
    SurfaceContainerHigh,
    SurfaceContainerHighest,
    OnSurface,
    OnSurfaceVariant,
    Outline,
    OutlineVariant,
    Scrim,
    AmountGreen,
    AlertOrange,
    VividBlue,
    VividRed,
    VividGreen,
    VividPurple,
    VividYellow,
    BlueToWhite,
    GreenToBlue,
    RedToWhite,
    LightGrayToDarkBlue,
    TintIconColor
}

val keyboardListenerFlow = MutableSharedFlow<Boolean>(extraBufferCapacity = 1, onBufferOverflow = BufferOverflow.DROP_OLDEST)
